import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { Resizable, ResizeCallbackData } from 'react-resizable';
import Icon from '@ant-design/icons';
import { WidgetState, WidgetPortContext } from '@kemu-io/kemu-core/types';
import { WidgetPort } from '@kemu-io/hs-types';
import ExpressionEvalWidgetProcessor, {
	ExpressionEvalWidgetState,
	getDefaultState
} from '@kemu-io/kemu-core/widgets/expressionEval/index.js';
import GateIcon from '../../gateIcon/gateIcon';
import {
	GetPortsInformationFunction,
	GateUI,
	GateUIProps,
} from '../index.ts';
import { PortLocation } from '../../../types/canvas_t';
import styles from './expressionEval.module.css';
import { ReactComponent as ExpressionEvalWidgetIcon } from './icon.svg';
import useReactiveWidgetState from '@hooks/useReactiveWidgetState';
import { ABORT_DRAGGING_CLASS } from '@common/constants';
import 'react-resizable/css/styles.css';

const DEFAULT_W = 150;
const DEFAULT_H = 40;



const ExpressionEvalWidgetUI = (props: GateUIProps): React.JSX.Element => {
	const { repaintExistingPorts } = props;
	const textAreaRef = useRef<HTMLTextAreaElement>(null);
	const [state, setState] = useReactiveWidgetState<ExpressionEvalWidgetState>(props.recipeId, props.thingRecipeId, props.info.id);
	const fixedState: ExpressionEvalWidgetState = useMemo(() => ({
		...getDefaultState(),
		...state
	}), [state]);

	const handleResize = useCallback((event: React.SyntheticEvent<Element, Event>, data: ResizeCallbackData) => {
		setState(current => ({
			...current,
			size: {
				width: data.size.width,
				height: data.size.height
			}
		}));
		repaintExistingPorts();
	}, [repaintExistingPorts, setState]);

	const handleTextChange: React.ChangeEventHandler<HTMLTextAreaElement> = (event) => {
		const newExpression = event.currentTarget.value;
		setState(currentCoreState => ({
			...currentCoreState,
			expression: newExpression
		}));
	};


	return (
		<Resizable
			height={fixedState.size?.height || DEFAULT_H}
			width={fixedState.size?.width || DEFAULT_W}
			minConstraints={[DEFAULT_W, DEFAULT_H]}
			onResize={handleResize}
		>
			<div className={`${styles.GateBody} ${ABORT_DRAGGING_CLASS}`}
				style={{
					width: (fixedState.size?.width || DEFAULT_W),
					height: (fixedState.size?.height || DEFAULT_H)
				}}
			>
				<textarea ref={textAreaRef}
					defaultValue={ fixedState.expression }
					onChange={handleTextChange}
					placeholder="Enter expression..."
				/>
			</div>
		</Resizable>
	);
};

/** Icon to be added to the bar */
const GateBarIcon = (): React.JSX.Element => {
	return (
		<GateIcon icon={<Icon component={ExpressionEvalWidgetIcon} />}/>
	);
};


const getPortsInformation: GetPortsInformationFunction = (state: WidgetState<ExpressionEvalWidgetState>, widgetInfo) => {
	const portContext: WidgetPortContext = {
		recipePoolId: widgetInfo.recipePoolId,
		recipeType: widgetInfo.recipeType,
		thingRecipeId: widgetInfo.thingRecipeId,
		widgetId: widgetInfo.id,
	};
	const outputNames = ExpressionEvalWidgetProcessor.getOutputNames(state, portContext);
	const inputNames = ExpressionEvalWidgetProcessor.getInputNames(state, portContext);

	const inputPositions: Record<string, PortLocation> = {
		'data': [0, 0.5, -1, 0], // Centered on the left
	};

	const outputPositions: Record<string, PortLocation> = {
		'result': [1, 0.5, 1, 0], // Centered on the right
	};

	return {
		inputs: inputNames.map((input: WidgetPort) => ({
			name: input.name,
			type: input.type,
			position: inputPositions[input.name],
		})),

		outputs: outputNames.map((output: WidgetPort) => ({
			name: output.name,
			type: output.type,
			position: outputPositions[output.name],
		})),
	};
};

export default {
	getPortsInformation,
	BarIcon: GateBarIcon,
	Element: ExpressionEvalWidgetUI,
	hasTitle: true,
	getWidgetTitle: (intl) => intl.formatMessage({ id: 'LogicMapper.Widgets.ExpressionEval.Title' }),
	getGatesBarTitle: (intl) => intl.formatMessage({ id: 'LogicMapper.Widgets.ExpressionEval.Title' }),
} as GateUI;
